import React, { useState, Suspense } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { usePriceInfo, formatPrice } from './PriceInfo';

// 🔧 修正: 分割されたダイアログをlazy import
const PriceEditDialogLazy = React.lazy(() => 
  import('./PriceEditDialog').then(module => ({ 
    default: module.PriceEditDialog 
  }))
);

const PriceCreateDialogLazy = React.lazy(() => 
  import('./PriceCreateDialog').then(module => ({ 
    default: module.PriceCreateDialog 
  }))
);

/**
 * 🔧 修正版：価格情報セクションコンポーネント
 * - 既存データ編集と新規作成を分離
 * - ProductDetailsから分離した価格表示部分
 */
const PriceSection = ({ 
  selectedProduct, 
  priceRefreshTrigger = 0,
  onPriceUpdateSuccess,
  onPriceUpdateError 
}) => {
  const { customPrice, standardPrice, loading, error, refetch } = usePriceInfo(
    selectedProduct, 
    priceRefreshTrigger
  );
  
  const [showDetails, setShowDetails] = useState(false);
  const [priceEditOpen, setPriceEditOpen] = useState(false);

  // 価格編集ハンドラー（既存データのみ）
  const handlePriceEdit = () => {
    if (selectedProduct?.id) {
      setPriceEditOpen(true);
    } else {
    }
  };

  // 価格更新成功時の処理
  const handlePriceUpdateSuccess = (message) => {
    onPriceUpdateSuccess?.(message);
    setPriceEditOpen(false);
  };

  // 価格更新エラー時の処理
  const handlePriceUpdateError = (message) => {
    onPriceUpdateError?.(message);
    setPriceEditOpen(false);
  };

  if (error) {
    return (
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={refetch}>
              再試行
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card elevation={2} sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            単価情報を読み込み中...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // 適用価格の判定
  const isUsingCustomPrice = customPrice !== null;
  const appliedPrice = isUsingCustomPrice ? customPrice : standardPrice;
  const hasAnyPrice = appliedPrice !== null;

  return (
    <>
      <Card elevation={2} sx={{ 
        borderRadius: 2, 
        mb: 2,
        borderColor: isUsingCustomPrice ? 'success.main' : 
                     hasAnyPrice ? 'primary.main' : 'warning.main',
        borderWidth: 1,
        borderStyle: 'solid'
      }}>
        <CardContent sx={{ p: 0 }}>
          {/* コンパクトなヘッダー */}
          <Box 
            sx={{ 
              bgcolor: isUsingCustomPrice ? 'success.50' : 
                       hasAnyPrice ? 'primary.50' : 'warning.50',
              p: 1.5, 
              borderBottom: showDetails ? 1 : 0, 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="medium" color="text.secondary">
                取引情報
              </Typography>
            </Box>
            
            <Tooltip title="単価設定を編集">
              <IconButton 
                size="small" 
                color="primary"
                onClick={handlePriceEdit}
                sx={{ p: 0.5 }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* メイン単価表示（コンパクト） */}
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isUsingCustomPrice ? (
                  <LocalOfferIcon color="success" sx={{ fontSize: 20 }} />
                ) : hasAnyPrice ? (
                  <MonetizationOnIcon color="primary" sx={{ fontSize: 20 }} />
                ) : (
                  <MonetizationOnIcon color="warning" sx={{ fontSize: 20 }} />
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, display: 'block' }}>
                    適用単価
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color={hasAnyPrice ? 'text.primary' : 'warning.main'} sx={{ lineHeight: 1.2 }}>
                    {hasAnyPrice ? formatPrice(appliedPrice) : '未設定'}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                {isUsingCustomPrice ? (
                  <Chip 
                    label="顧客専用" 
                    color="success" 
                    size="small"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                ) : hasAnyPrice ? (
                  <Chip 
                    label="製品標準" 
                    color="primary" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                ) : (
                  <Chip 
                    label="要設定" 
                    color="warning" 
                    size="small"
                    variant="outlined"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      '& .MuiChip-label': { px: 0.8 }
                    }}
                  />
                )}
                
                {/* 詳細の折りたたみボタン（小さく） */}
                <Button
                  size="small"
                  onClick={() => setShowDetails(!showDetails)}
                  endIcon={showDetails ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  sx={{ 
                    p: 0,
                    minHeight: 'auto',
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    textTransform: 'none',
                    '& .MuiButton-endIcon': { ml: 0.5 }
                  }}
                >
                  詳細
                </Button>
              </Box>
            </Box>

            {/* 詳細情報（折りたたみ式・コンパクト） */}
            {showDetails && (
              <Box sx={{ 
                mt: 1.5,
                pt: 1, 
                borderTop: '1px solid', 
                borderColor: 'divider',
                bgcolor: 'rgba(0,0,0,0.02)',
                borderRadius: 1,
                p: 1
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 'medium' }}>
                  単価設定詳細
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">製品標準単価:</Typography>
                    <Typography 
                      variant="caption" 
                      fontWeight="medium"
                      color={standardPrice !== null ? (isUsingCustomPrice ? 'text.primary' : 'primary.main') : 'warning.main'}
                    >
                      {standardPrice !== null ? 
                        `${formatPrice(standardPrice)}${isUsingCustomPrice ? '' : ' ✓'}` : 
                        '未設定'
                      }
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="caption" 
                      color={isUsingCustomPrice ? 'success.main' : 'text.disabled'}
                    >
                      顧客専用単価:
                    </Typography>
                    <Typography 
                      variant="caption" 
                      fontWeight={isUsingCustomPrice ? 'bold' : 'normal'}
                      color={isUsingCustomPrice ? 'success.main' : 'text.disabled'}
                    >
                      {isUsingCustomPrice ? `${formatPrice(customPrice)} ✓` : '未設定'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 🔧 既存データ編集ダイアログ */}
      {priceEditOpen && (
        <Suspense fallback={<CircularProgress />}>
          <PriceEditDialogLazy
            open={priceEditOpen}
            onClose={() => setPriceEditOpen(false)}
            selectedProduct={selectedProduct}
            onSuccess={handlePriceUpdateSuccess}
            onError={handlePriceUpdateError}
          />
        </Suspense>
      )}
    </>
  );
};

export default PriceSection;